import { Delete } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import GenericModal from "src/components/GenericModal/GenericModal";
import { FunctionalGroup } from "src/types";

type MemberInfo = {
  organizationMembershipId: string;
  functionalGroupMembershipId: string;
  userName: string;
  email: string;
};

type AvailableUser = {
  organizationMembershipId: string;
  userName: string;
  email: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  group: FunctionalGroup | null;
  members: MemberInfo[];
  availableUsers: AvailableUser[];
  isSaving?: boolean;
  onAddMember: (organizationMembershipId: string) => Promise<void> | void;
  onRemoveMember: (membershipId: string) => Promise<void> | void;
};

type PendingMemberAction =
  | { type: "add"; user: AvailableUser }
  | { type: "remove"; member: MemberInfo };

export const ManageMembersModal = ({
  isOpen,
  onClose,
  group,
  members,
  availableUsers,
  isSaving = false,
  onAddMember,
  onRemoveMember,
}: Props) => {
  const [pendingAction, setPendingAction] =
    useState<PendingMemberAction | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPendingAction(null);
    }
  }, [isOpen]);

  if (!group) return null;

  const handleClose = () => {
    setPendingAction(null);
    onClose();
  };

  const handleSave = async () => {
    if (!pendingAction) {
      handleClose();
      return;
    }

    if (pendingAction.type === "add") {
      await onAddMember(pendingAction.user.organizationMembershipId);
    } else {
      await onRemoveMember(pendingAction.member.functionalGroupMembershipId);
    }

    setPendingAction(null);
  };

  const handleCancel = () => {
    if (pendingAction) {
      setPendingAction(null);
      return;
    }

    handleClose();
  };

  const pendingPerson =
    pendingAction?.type === "add"
      ? pendingAction.user.userName || pendingAction.user.email
      : pendingAction?.member.userName || pendingAction?.member.email;

  return (
    <GenericModal
      isOpen={isOpen}
      handleCancel={handleCancel}
      handleSave={handleSave}
      title={`Manage Members - ${group.name}`}
      saveText={pendingAction ? "Confirm" : "Done"}
      cancelText={pendingAction ? "Cancel" : "Close"}
      isLoading={isSaving}
      hideActions={false}
    >
      {pendingAction ? (
        <div className="flex flex-col gap-3">
          <Typography variant="subtitle2">
            {pendingAction.type === "add" ? "Add member" : "Remove member"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pendingPerson} - {group.name}
          </Typography>
          <Alert severity="warning" sx={{ fontSize: "0.875rem" }}>
            {pendingAction.type === "add"
              ? "Users will receive all trainings currently assigned to this functional group. Are you sure?"
              : "User will have all pending trainings deleted upon removal from this group. Are you sure?"}
          </Alert>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <Typography variant="subtitle2">Add Member</Typography>
          <Autocomplete
            options={availableUsers}
            getOptionLabel={(option) =>
              option.userName
                ? `${option.userName} (${option.email})`
                : option.email
            }
            renderInput={(params) => (
              <TextField {...params} label="Search users" size="small" />
            )}
            onChange={(_, value) => {
              if (value) {
                setPendingAction({ type: "add", user: value });
              }
            }}
            value={null}
            isOptionEqualToValue={(option, value) =>
              option.organizationMembershipId === value.organizationMembershipId
            }
            size="small"
          />

          <Typography variant="subtitle2">
            Current Members ({members.length})
          </Typography>
          {members.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No members yet.
            </Typography>
          ) : (
            <List dense>
              {members.map((member) => (
                <ListItem
                  key={member.functionalGroupMembershipId}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label={`remove ${member.userName || member.email}`}
                      onClick={() =>
                        setPendingAction({ type: "remove", member })
                      }
                      size="small"
                    >
                      <Delete fontSize="small" color="error" />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={member.userName || member.email}
                    secondary={member.userName ? member.email : undefined}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </div>
      )}
    </GenericModal>
  );
};
